
import { mapActions, mapGetters } from 'vuex';
import { isPortfolioOwner } from '@/utils/portfolio-role-checks';
import UICreatePortfolioModalv2 from '@/components/ui/organism/UICreatePortfolioModalv2';
import UICreateLeaguePortfolioModal from '@/components/ui/organism/UICreateLeaguePortfolioModal';
import {
  canCreatePortfolio,
  cannotCreatePortfolioReason,
  portfolioLimitReachedError,
  portfolioUniqueNameError,
} from '~/utils/behavior/portfolio-creation';
import { startTransactionAsync } from '~/utils/instrumentation/transactions';
import { showErrorToast } from '~/utils/toast-notifications';
import { ApiApplicationErrorCodes } from '~/utils/behavior/apiErrors';
import { getApplicationError } from '~/utils/behavior/apiErrors';
import { inAppRedirect } from '~/utils/route-redirects';
import { eventManager } from '@/utils/behavior/applicationEvents/event-manager';

export default {
  name: 'CreatePortfolioModalWrapper',
  components: { UICreatePortfolioModalv2, UICreateLeaguePortfolioModal },
  computed: {
    ...mapGetters('portfolio', ['createPortfolioRes', 'getMyPortfoliosRes']),
    ...mapGetters('modals', ['createPortfolioModal']),
    ...mapGetters('authentication', [
      'isEmailConfirmed',
      'profileId',
      'isVerifiedInfluencer',
    ]),
    ...mapGetters('profile', ['hasSeenWTPModalRes', 'profile']),
    ...mapGetters('quickstart', ['progress']),
    myPortfolios() {
      return (
        this.getMyPortfoliosRes?.data?.portfolios?.filter((p) =>
          isPortfolioOwner(p.portfolioUserRole)
        ) || []
      );
    },
    canCreateNewPortfolio() {
      return canCreatePortfolio(
        this.isEmailConfirmed,
        this.myPortfolios.length,
        this.isVerifiedInfluencer
      );
    },
    disabledReason() {
      return cannotCreatePortfolioReason(
        this.isEmailConfirmed,
        this.myPortfolios.length,
        this.isVerifiedInfluencer
      );
    },
  },
  methods: {
    ...mapActions('portfolio', ['createPortfolio']),
    ...mapActions('modals', [
      'showCreatePortfolioModal',
      'showPortfolioEmptyStateModal',
    ]),
    ...mapActions('profile', ['getProfile']),
    ...mapActions('leagues', ['getLeagueRulesById']),
    async handleCreate(data) {
      try {
        // raise event about portfolio creation started
        eventManager.raisePortfolioCreationStartedEvent();

        await startTransactionAsync('Create Portfolio', async () => {
          await this.createPortfolio({
            name: data.name,
            description: data.description,
            isPublic: data.privacy == 'public',
            currencyCode: data.currency,
            type: data.type,
            cashAmounts: data.cashAmounts,
            date: data.date,
            // Will be null if not creating a league portfolio
            leagueId: this.createPortfolioModal.leagueId,
            referralCode: data.referralCode,
          });

          // TODO currently our api actions aren't consistent and dont allow errors to bubble up
          // so we can't catch errors here. A JIRA ticket should be created to handle this
          if (!this.createPortfolioRes.data)
            throw new Error('Failed to create portfolio');

          // raise event about successful portfolio creation
          eventManager.raisePortfolioCreatedEvent();

          inAppRedirect('/portfolios/' + this.createPortfolioRes.data.id);
        });
      } catch (e) {
        console.error('Create portfolio transaction failed', e);

        // raise event about failed portfolio creation
        eventManager.raisePortfolioCreationFailedEvent();

        const apiErrorCode = String(this.createPortfolioRes?.error?.errorCode);
        const errorCode = getApplicationError(apiErrorCode);

        let errorTranslationKey = 'error_occured_message';

        switch (errorCode) {
          case ApiApplicationErrorCodes.BadRequest
            .MaximumPortfoliosPerProfileReached:
            errorTranslationKey = portfolioLimitReachedError;
            break;
          case ApiApplicationErrorCodes.BadRequest.UniquePortfolioName:
            errorTranslationKey = portfolioUniqueNameError;
            break;
        }

        showErrorToast(this.$t(errorTranslationKey));
      }
    },
    handleClose() {
      this.showCreatePortfolioModal();
    },
  },
};
